import React, { useState, useCallback, useMemo } from 'react';
import type {
  AutomationFormData,
  AutomationFormLabels,
  AutomationFormProps,
  PeriodOption,
  PeriodUnitOption,
  FieldGroupOption,
} from '../../shared/types/automationForm';

export type { AutomationFormData, AutomationFormLabels, AutomationFormProps };

const WEEKLY_PERIODS = ['every_week'];
const CUSTOM_PERIOD = 'custom';

export function AutomationForm({
  initialData,
  onSubmit,
  onCancel,
  errors = {},
  labels: l,
  mode,
  periodOptions,
  periodUnitOptions,
  fieldGroups,
  isSubmitting = false,
}: AutomationFormProps) {
  const [formData, setFormData] = useState<AutomationFormData>(initialData);

  const handleFieldChange = useCallback((fieldId: string) => {
    setFormData(prev => ({ ...prev, fieldId }));
  }, []);

  const handlePeriodNameChange = useCallback((periodName: string) => {
    setFormData(prev => ({
      ...prev,
      periodName,
      // Reset fields when period changes
      periodUnit: periodName === CUSTOM_PERIOD ? (prev.periodUnit || 'days') : undefined,
      periodStep: periodName === CUSTOM_PERIOD ? (prev.periodStep || 1) : undefined,
    }));
  }, []);

  const handlePeriodUnitChange = useCallback((periodUnit: string) => {
    setFormData(prev => ({ ...prev, periodUnit }));
  }, []);

  const handlePeriodStepChange = useCallback((periodStep: number) => {
    setFormData(prev => ({ ...prev, periodStep }));
  }, []);

  const handleStartDateChange = useCallback((startDate: string) => {
    setFormData(prev => ({ ...prev, startDate }));
  }, []);

  const handleWeekdayChange = useCallback((day: keyof AutomationFormData, checked: boolean) => {
    setFormData(prev => ({ ...prev, [day]: checked }));
  }, []);

  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  }, [formData, onSubmit]);

  // Determine which conditional fields to show
  const isCustomPeriod = formData.periodName === CUSTOM_PERIOD;
  const isWeeklyPeriod = useMemo(() => {
    if (WEEKLY_PERIODS.includes(formData.periodName)) return true;
    if (isCustomPeriod && formData.periodUnit === 'weeks') return true;
    return false;
  }, [formData.periodName, formData.periodUnit, isCustomPeriod]);

  const showStartDate = isCustomPeriod && (formData.periodStep || 1) > 1;

  const dayLabels = [
    { key: 'onSunday' as const, label: l.sun },
    { key: 'onMonday' as const, label: l.mon },
    { key: 'onTuesday' as const, label: l.tue },
    { key: 'onWednesday' as const, label: l.wed },
    { key: 'onThursday' as const, label: l.thu },
    { key: 'onFriday' as const, label: l.fri },
    { key: 'onSaturday' as const, label: l.sat },
  ];

  return (
    <form onSubmit={handleSubmit}>
      {/* Error messages */}
      {Object.keys(errors).length > 0 && (
        <div className="alert alert-danger mb-3">
          <ul className="mb-0">
            {Object.entries(errors).flatMap(([field, messages]) =>
              messages.map((msg, i) => (
                <li key={`${field}-${i}`}>{msg}</li>
              ))
            )}
          </ul>
        </div>
      )}

      {/* Field selection */}
      <div className="form-group row mb-3">
        <div className="col-12">
          <label className="form-label">{l.automaticallyMarkActivity}</label>
          <select
            className="form-select"
            value={formData.fieldId}
            onChange={(e) => handleFieldChange(e.target.value)}
            required
          >
            <option value="">{l.selectActivity}</option>
            {fieldGroups.map((group) => (
              <optgroup key={group.id} label={group.name}>
                {group.fields.map((field) => (
                  <option key={field.id} value={field.id}>
                    {field.name}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>
      </div>

      {/* Period selection */}
      <div className="form-group row mb-3">
        <div className="col-12">
          <label className="form-label">{l.period}</label>
          <select
            className="form-select"
            value={formData.periodName}
            onChange={(e) => handlePeriodNameChange(e.target.value)}
            required
          >
            {periodOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Custom period fields */}
      {isCustomPeriod && (
        <div className="form-group row mb-3">
          <div className="col-12">
            <label className="form-label">{l.every}</label>
            <div className="d-flex gap-2 align-items-center">
              <input
                type="number"
                className="form-control"
                style={{ width: '80px' }}
                min={1}
                step={1}
                value={formData.periodStep || 1}
                onChange={(e) => handlePeriodStepChange(parseInt(e.target.value) || 1)}
              />
              <select
                className="form-select"
                style={{ width: '140px' }}
                value={formData.periodUnit || 'days'}
                onChange={(e) => handlePeriodUnitChange(e.target.value)}
              >
                {periodUnitOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Weekday selection */}
      {isWeeklyPeriod && (
        <div className="form-group row mb-3">
          <div className="col-12">
            <label className="form-label">{l.repeatOnWeekDays}</label>
            <div className="d-flex flex-wrap gap-3">
              {dayLabels.map(({ key, label }) => (
                <div key={key} className="form-check form-check-inline">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id={key}
                    checked={formData[key] as boolean}
                    onChange={(e) => handleWeekdayChange(key, e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor={key}>
                    {label}
                  </label>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Start date */}
      {showStartDate && (
        <div className="form-group row mb-3">
          <div className="col-12">
            <label className="form-label">{l.startDate}</label>
            <input
              type="date"
              className="form-control"
              value={formData.startDate || ''}
              onChange={(e) => handleStartDateChange(e.target.value)}
            />
          </div>
        </div>
      )}

      {/* Form Buttons */}
      <div className="form-group d-flex justify-content-end buttons-wrapper">
        <button
          type="button"
          className="btn btn-link me-3"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          {l.cancel}
        </button>
        <button
          type="submit"
          className="btn btn-primary"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              {l.save}
            </>
          ) : (
            l.save
          )}
        </button>
      </div>
    </form>
  );
}

export default AutomationForm;
